import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@mommy/environments/environment';
import { ExpertInfo } from '@mommy/models/ExpertInfo.model';
import { HospitalInfo } from '@mommy/models/HospitalInfo.model';
import { PostInfo } from '@mommy/models/PostInfo.model';
import { CommodityInfo } from '@mommy/models/ec/CommodityInfo.model';
import { AppSettings } from 'app/app.settings';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {
    // this.meta.addTags([
    //   {
    //     name: 'description',
    //     content: AppSettings.SEO_DEFAULT_DESCRIPTION,
    //   },
    //   { charset: 'UTF-8' },
    // ]);

    // 🔥 改用 updateTag 避免重複標籤
    this.meta.updateTag({
      name: 'description',
      content: AppSettings.SEO_DEFAULT_DESCRIPTION,
    });
    this.meta.updateTag({ charset: 'UTF-8' });

    this.title.setTitle(AppSettings.SEO_DEFAULT_TITLE);
  }

  /**
   * 格式化電話號碼為國際格式
   * @param phone 原始電話號碼（例：02-29589681, 0912345678）
   * @returns 國際格式電話號碼（例：+886-2-29589681, +886-912345678）
   */
  private formatPhoneNumber(phone: string): string {
    if (!phone) return '';

    // 移除所有空格和特殊字元
    let cleaned = phone.replace(/[\s\-()]/g, '');

    // 如果開頭是 0，轉換為 +886
    if (cleaned.startsWith('0')) {
      cleaned = '+886' + cleaned.substring(1);
    }

    // 如果是市話（+886 後面接 1-2 位數區碼），加入分隔符
    if (cleaned.match(/^\+886[1-9]\d{7,8}$/)) {
      return cleaned.replace(/^(\+886)(\d{1,2})(\d+)$/, '$1-$2-$3');
    }

    return cleaned;
  }

  /**
   * 轉換日期為 ISO 8601 格式
   * @param date 日期物件或字串
   * @returns ISO 8601 格式字串（例：2024-10-15T08:00:00+08:00）
   */
  private formatDateToISO(date: any): string {
    if (!date) return '';

    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';

      return dateObj.toISOString();
    } catch (error) {
      return '';
    }
  }

  /**
   * 轉換影片時長為 ISO 8601 Duration 格式
   * @param duration 時長字串（例：10:30, 1:05:30）
   * @returns ISO 8601 Duration（例：PT10M30S, PT1H5M30S）
   */
  private formatVideoDuration(duration: string): string {
    if (!duration) return '';

    try {
      const parts = duration.split(':').map(Number);
      let result = 'PT';

      if (parts.length === 3) {
        // HH:MM:SS
        if (parts[0] > 0) result += `${parts[0]}H`;
        if (parts[1] > 0) result += `${parts[1]}M`;
        if (parts[2] > 0) result += `${parts[2]}S`;
      } else if (parts.length === 2) {
        // MM:SS
        if (parts[0] > 0) result += `${parts[0]}M`;
        if (parts[1] > 0) result += `${parts[1]}S`;
      }

      return result === 'PT' ? '' : result;
    } catch (error) {
      return '';
    }
  }

  /**
   * 更新或建立結構化資料（JSON-LD）
   * @param data Schema.org 結構化資料物件
   */
  private updateStructuredData(data: any) {
    if (!data) return;

    // 統一使用固定的 ID
    const structuredDataId = 'page-structured-data';

    // 移除所有現有的結構化資料標籤（避免不同頁面類型的資料混在一起）
    const existingElements = this.document.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    existingElements.forEach((el) => {
      // 只移除我們自己建立的標籤（有特定 ID 的）
      if (el.id && el.id.includes('structured-data')) {
        el.remove();
      }
    });

    // 建立新的 JSON-LD script 標籤
    const scriptElement = this.document.createElement('script');
    scriptElement.id = structuredDataId;
    scriptElement.type = 'application/ld+json';
    scriptElement.textContent = JSON.stringify(data, null, 2);
    this.document.head.appendChild(scriptElement);
  }

  updateTitle(title: string) {
    if (title) {
      this.title.setTitle(title);
    } else {
      this.title.setTitle(AppSettings.SEO_DEFAULT_TITLE);
    }
  }

  updateDescription(description: string) {
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    } else {
      this.meta.updateTag({
        name: 'description',
        content: AppSettings.SEO_DEFAULT_DESCRIPTION,
      });
    }
  }

  /**
   * 移除 HTML 標籤
   * @param html 包含 HTML 標籤的字串
   * @returns 清理後的純文字
   */
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    const regex = /<[^>]*>/gim;
    return html.replace(regex, '').trim();
  }

  /**
   * 截斷描述文字至指定長度
   * @param text 原始文字
   * @param maxLength 最大長度（預設 200 字元）
   * @returns 截斷後的文字
   */
  private truncateDescription(text: string, maxLength: number = 200): string {
    if (!text) return '';

    const cleaned = text.trim();
    if (cleaned.length <= maxLength) {
      return cleaned;
    }

    // 截斷並加上省略符號
    return cleaned.substring(0, maxLength) + '...';
  }

  /**
   * 取得絕對路徑的圖片 URL
   * @param imageUrl 圖片 URL（可能是相對路徑或絕對路徑）
   * @param allowDefault 是否允許返回預設圖片（用於 OG meta）
   * @returns 完整的絕對路徑 URL，如果是預設圖片且 allowDefault=false 則返回空字串
   */
  private getAbsoluteImageUrl(
    imageUrl: string,
    allowDefault: boolean = true
  ): string {
    if (!imageUrl) {
      // 如果不允許預設值，返回空字串
      if (!allowDefault) return '';

      // 否則返回預設 OG 圖片（用於社交分享）
      return `${environment.BASE_WEBSITE_URL}${AppSettings.SEO_DEFAULT_OG_IMAGE}`;
    }

    // 檢查是否為真實圖片（Google Storage）
    const isRealImage = imageUrl.includes('storage.googleapis.com');

    // 如果不允許預設圖且不是真實圖片，返回空字串
    if (!allowDefault && !isRealImage) {
      return '';
    }

    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      // 已是完整 URL → 直接使用
      return imageUrl;
    }

    // 相對路徑 → 加上網域前綴
    const prefix = imageUrl.startsWith('/') ? '' : '/';
    return `${environment.BASE_WEBSITE_URL}${prefix}${imageUrl}`;
  }

  /**
   * 判斷作者是否為藥師（根據名稱判斷）
   * @param authorName 作者名稱
   * @returns 是否為藥師
   */
  private isPharmacistAuthor(authorName: string): boolean {
    if (!authorName) return false;

    // 明確定義小編名稱
    const editorNames = ['全馨護編輯群', '馨關懷'];

    // 如果是小編，返回 false
    if (editorNames.includes(authorName)) {
      return false;
    }

    // 其他都視為藥師
    return true;
  }

  /**
   * 更新 Open Graph meta 標籤
   * @param params OG meta 參數
   */
  private updateOGTags(params: {
    title: string;
    description: string;
    image: string;
    url: string;
    type?: string;
  }) {
    const ogType = params.type || 'website';

    this.meta.updateTag({ property: 'og:title', content: params.title });
    this.meta.updateTag({
      property: 'og:description',
      content: params.description,
    });
    this.meta.updateTag({ property: 'og:image', content: params.image });
    this.meta.updateTag({ property: 'og:url', content: params.url });
    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({
      property: 'og:site_name',
      content: AppSettings.SEO_SITE_NAME,
    });
    this.meta.updateTag({ property: 'og:locale', content: 'zh_TW' });
  }

  /**
   * 更新 Twitter Card meta 標籤
   * @param params Twitter meta 參數
   */
  private updateTwitterTags(params: {
    title: string;
    description: string;
    image: string;
  }) {
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: params.title });
    this.meta.updateTag({
      name: 'twitter:description',
      content: params.description,
    });
    this.meta.updateTag({ name: 'twitter:image', content: params.image });
  }

  /**
   * 更新 canonical 標籤
   * @param url 完整的 canonical URL（會自動移除查詢參數）
   */
  private updateCanonical(url: string) {
    if (!url) {
      return;
    }

    // 移除查詢參數，確保 canonical URL 是正規化的
    const canonicalUrl = url.split('?')[0];

    // 查找現有的 canonical 標籤
    let linkElement: HTMLLinkElement = this.document.querySelector(
      'link[rel="canonical"]'
    );

    if (linkElement) {
      // 更新現有標籤
      linkElement.setAttribute('href', canonicalUrl);
    } else {
      // 建立新的 canonical 標籤
      linkElement = this.document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      linkElement.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(linkElement);
    }
  }

  setPostMeta(post: PostInfo) {
    if (post) {
      const title = post.post_title + AppSettings.SEO_DEFAULT_SUFFIX;
      const description = post.post_excerpt || '';

      this.updateTitle(title);
      this.updateDescription(description);

      // 根據 post_type 設定對應的 canonical URL
      if (post.post_id) {
        let postPath = '';
        switch (post.post_type) {
          case 'pgc專家':
            postPath = 'pgc-post';
            break;
          case 'pedia百科':
            postPath = 'pedia-post';
            break;
          case 'video視頻':
            postPath = 'video-post';
            break;
          case '能不能吃':
            postPath = 'canieat-post';
            break;
          default:
            // 如果 post_type 不匹配，不設定 canonical
            return;
        }
        const canonicalUrl = `${environment.BASE_WEBSITE_URL}/${postPath}/${post.post_id}`;
        this.updateCanonical(canonicalUrl);

        // 設定 Open Graph 和 Twitter Card meta 標籤
        const imageUrl = this.getAbsoluteImageUrl(post.post_feature_image_url);
        this.updateOGTags({
          title,
          description,
          image: imageUrl,
          url: canonicalUrl,
          type: 'article',
        });
        this.updateTwitterTags({
          title,
          description,
          image: imageUrl,
        });

        // 設定結構化資料 (依據 post_type)
        this.setPostStructuredData(post, canonicalUrl, imageUrl);
      }
    }
  }

  /**
   * 設定文章的結構化資料 (依據類型動態生成)
   * @param post 文章資訊
   * @param canonicalUrl 正規化 URL
   * @param imageUrl 圖片 URL（可能包含預設圖）
   */
  private setPostStructuredData(
    post: PostInfo,
    canonicalUrl: string,
    imageUrl: string
  ) {
    // 暫時不為影片生成結構化資料（網站影片數量少）
    if (post.post_type === 'video視頻') {
      return;
    }

    // MedicalWebPage 結構 (適用於 pgc專家、pedia百科)
    const structuredData: any = {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      headline: post.post_title,
      description: post.post_excerpt || '',
      url: canonicalUrl,
      mainEntityOfPage: canonicalUrl,
      datePublished: this.formatDateToISO(post.post_date),
      dateModified: this.formatDateToISO(post.post_modified_at),
    };

    // 圖片（只有真實圖片才加入）
    const realImageUrl = this.getAbsoluteImageUrl(
      post.post_feature_image_url,
      false
    );
    if (realImageUrl) {
      structuredData.image = realImageUrl;
    }

    // 作者資訊 (如果有)
    if (post.post_author && post.post_author.expert_name) {
      // 判斷是否為藥師（根據名稱判斷）
      const isPharmacist = this.isPharmacistAuthor(
        post.post_author.expert_name
      );

      structuredData.author = {
        '@type': 'Person',
        name: post.post_author.expert_name,
      };
      if (isPharmacist) {
        structuredData.author.jobTitle = '藥師';
      }

      // 如果有作者 ID，加入 URL
      if (post.post_author.expert_id) {
        structuredData.author.url = `${environment.BASE_WEBSITE_URL}/expert/${post.post_author.expert_id}`;
      }

      // 如果有作者頭像（且是真實圖片）
      if (post.post_author.expert_avatar_url) {
        const authorImageUrl = this.getAbsoluteImageUrl(
          post.post_author.expert_avatar_url,
          false
        );
        if (authorImageUrl) {
          structuredData.author.image = authorImageUrl;
        }
      }
    } else {
      // 沒有特定作者時使用組織
      structuredData.author = {
        '@type': 'Organization',
        name: '全馨護醫療團隊',
      };
    }

    // 發布者資訊
    structuredData.publisher = {
      '@type': 'Organization',
      name: AppSettings.SEO_SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${environment.BASE_WEBSITE_URL}/assets/icon/favicon.png`,
      },
    };

    // 更新結構化資料
    this.updateStructuredData(structuredData);
  }

  setHospitalMeta(hospital: HospitalInfo) {
    if (hospital) {
      // for 地區SEO, 判斷 top_categorys 是否有 血液透析 的分類
      // 如果有, 則 Title: {{診所名稱}}｜{{地區}} 洗腎診所・透析中心專業照護 | 全馨護xxx
      let isDialysis = false;
      if (hospital.top_categorys) {
        for (const category of hospital.top_categorys) {
          if (category.taxonomy_name === '血液透析') {
            isDialysis = true;
            break;
          }
        }
      }

      let title = hospital.hospital_name;
      if (isDialysis) {
        title +=
          '｜' +
          hospital.city +
          hospital.district +
          ' 洗腎診所・透析中心專業照護';
      } else {
        title += '｜' + hospital.city + hospital.district + ' 醫療院所';
      }

      const fullTitle = title + AppSettings.SEO_DEFAULT_SUFFIX;
      this.updateTitle(fullTitle);

      // 建立描述文字
      let desc = hospital.hospital_name;
      if (isDialysis) {
        desc +=
          '位於' +
          hospital.city +
          hospital.district +
          '，提供完整的洗腎與透析照護服務。';
      } else {
        desc +=
          '位於' + hospital.city + hospital.district + '，提供完整的醫療服務。';
      }

      if (hospital.hospital_desc) {
        const cleanedDesc = this.stripHtmlTags(hospital.hospital_desc);
        desc += cleanedDesc;
      }

      // 截斷描述至 200 字元
      const truncatedDesc = this.truncateDescription(desc, 200);
      this.updateDescription(truncatedDesc);

      // 設定 canonical URL 和 OG/Twitter meta
      if (hospital.hospital_id) {
        const canonicalUrl = `${environment.BASE_WEBSITE_URL}/hospital/${hospital.hospital_id}`;
        this.updateCanonical(canonicalUrl);

        // 優先使用 hospital_avatar_url，若無則使用 hospital_feature_image_url[0]
        let imageUrl = hospital.hospital_avatar_url;
        if (
          !imageUrl &&
          hospital.hospital_feature_image_url &&
          hospital.hospital_feature_image_url.length > 0
        ) {
          imageUrl = hospital.hospital_feature_image_url[0];
        }
        const absoluteImageUrl = this.getAbsoluteImageUrl(imageUrl);

        this.updateOGTags({
          title: fullTitle,
          description: truncatedDesc,
          image: absoluteImageUrl,
          url: canonicalUrl,
        });
        this.updateTwitterTags({
          title: fullTitle,
          description: truncatedDesc,
          image: absoluteImageUrl,
        });

        // 設定結構化資料 (Schema.org - MedicalClinic)
        this.setHospitalStructuredData(
          hospital,
          canonicalUrl,
          truncatedDesc,
          absoluteImageUrl
        );
      }
    }
  }

  /**
   * 設定診所/藥局的結構化資料 (Schema.org - MedicalClinic)
   * @param hospital 診所資訊
   * @param canonicalUrl 正規化 URL
   * @param description 描述文字
   * @param imageUrl 圖片 URL（可能包含預設圖）
   */
  private setHospitalStructuredData(
    hospital: HospitalInfo,
    canonicalUrl: string,
    description: string,
    imageUrl: string
  ) {
    const structuredData: any = {
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      name: hospital.hospital_name,
      description: description,
      url: canonicalUrl,
    };

    // 圖片（只有真實圖片才加入）
    // 優先使用 hospital_avatar_url，若無則使用 hospital_feature_image_url[0]
    let realImageUrl = '';
    if (hospital.hospital_avatar_url) {
      realImageUrl = this.getAbsoluteImageUrl(
        hospital.hospital_avatar_url,
        false
      );
    }
    if (
      !realImageUrl &&
      hospital.hospital_feature_image_url &&
      hospital.hospital_feature_image_url.length > 0
    ) {
      realImageUrl = this.getAbsoluteImageUrl(
        hospital.hospital_feature_image_url[0],
        false
      );
    }
    if (realImageUrl) {
      structuredData.image = realImageUrl;
    }

    // 地址資訊
    if (hospital.address || hospital.city || hospital.district) {
      structuredData.address = {
        '@type': 'PostalAddress',
        streetAddress: hospital.address || '',
        addressLocality: hospital.district || '',
        addressRegion: hospital.city || '',
        postalCode: hospital.zip_code || '',
        addressCountry: 'TW',
      };
    }

    // 地理座標
    if (hospital.latitude && hospital.longitude) {
      structuredData.geo = {
        '@type': 'GeoCoordinates',
        latitude: hospital.latitude,
        longitude: hospital.longitude,
      };
    }

    // 聯絡電話
    if (hospital.phone_number) {
      structuredData.telephone = this.formatPhoneNumber(hospital.phone_number);
    }

    // Email
    if (hospital.email) {
      structuredData.email = hospital.email;
    }

    // 社交媒體連結
    const socialLinks = [hospital.facebook_url, hospital.ig_url].filter(
      (url) => url
    );

    if (socialLinks.length > 0) {
      structuredData.sameAs = socialLinks;
    }

    // 更新結構化資料
    this.updateStructuredData(structuredData);
  }

  setExpertMeta(expert: ExpertInfo) {
    if (expert) {
      const title = expert.expert_name + AppSettings.SEO_DEFAULT_SUFFIX;
      this.updateTitle(title);

      // 清理 HTML 標籤並截斷描述
      const cleanedDesc = this.stripHtmlTags(expert.expertise);
      const truncatedDesc = this.truncateDescription(cleanedDesc, 200);
      this.updateDescription(truncatedDesc);

      // 設定 canonical URL 和 OG/Twitter meta
      if (expert.expert_id) {
        const canonicalUrl = `${environment.BASE_WEBSITE_URL}/expert/${expert.expert_id}`;
        this.updateCanonical(canonicalUrl);

        const imageUrl = this.getAbsoluteImageUrl(expert.expert_avatar_url);
        this.updateOGTags({
          title,
          description: truncatedDesc,
          image: imageUrl,
          url: canonicalUrl,
        });
        this.updateTwitterTags({
          title,
          description: truncatedDesc,
          image: imageUrl,
        });

        // 設定結構化資料 (Schema.org - Person)
        this.setExpertStructuredData(
          expert,
          canonicalUrl,
          truncatedDesc,
          imageUrl
        );
      }
    }
  }

  /**
   * 設定專家藥師的結構化資料 (Schema.org - Person)
   * @param expert 專家資訊
   * @param canonicalUrl 正規化 URL
   * @param description 描述文字
   * @param imageUrl 圖片 URL（可能包含預設圖）
   */
  private setExpertStructuredData(
    expert: ExpertInfo,
    canonicalUrl: string,
    description: string,
    imageUrl: string
  ) {
    // 判斷是否為藥師
    const isPharmacist = this.isPharmacistAuthor(expert.expert_name);

    const structuredData: any = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: expert.expert_name,
      url: canonicalUrl,
      description: description,
    };

    // 圖片（只有真實圖片才加入）
    const realImageUrl = this.getAbsoluteImageUrl(
      expert.expert_avatar_url,
      false
    );
    if (realImageUrl) {
      structuredData.image = realImageUrl;
    }

    // 職稱
    if (expert.job_title) {
      structuredData.jobTitle = expert.job_title;
    } else if (isPharmacist) {
      structuredData.jobTitle = '藥師'; // 預設值（僅當判斷為藥師時）
    }

    // 更新結構化資料
    this.updateStructuredData(structuredData);
  }

  setECCommodityMeta(commodity: CommodityInfo) {
    if (commodity) {
      const title = commodity.title + AppSettings.SEO_DEFAULT_SUFFIX;
      this.updateTitle(title);

      // 清理 HTML 標籤並截斷描述
      const cleanedDesc = this.stripHtmlTags(commodity.content);
      const truncatedDesc = this.truncateDescription(cleanedDesc, 200);
      this.updateDescription(truncatedDesc);

      // 設定 canonical URL 和 OG/Twitter meta
      if (commodity.id) {
        const canonicalUrl = `${environment.BASE_WEBSITE_URL}/ec-product/${commodity.id}`;
        this.updateCanonical(canonicalUrl);

        // 處理 EC 商品圖片：需要加上 CDN URL
        let imageUrl = '';
        if (commodity.imagePath) {
          imageUrl = `${environment.EC_IMAGE_CDN_URL}${commodity.imagePath}`;
        } else if (commodity.image && commodity.image.length > 0) {
          imageUrl = `${environment.EC_IMAGE_CDN_URL}${commodity.image[0].imagePath}`;
        }
        const absoluteImageUrl = this.getAbsoluteImageUrl(imageUrl);

        this.updateOGTags({
          title,
          description: truncatedDesc,
          image: absoluteImageUrl,
          url: canonicalUrl,
          type: 'product',
        });
        this.updateTwitterTags({
          title,
          description: truncatedDesc,
          image: absoluteImageUrl,
        });
      }
    }
  }
}
